
import { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, Snackbar, SnackbarCloseReason} from '@mui/material';
import { BasicSelectUF, IVFormErrors, VForm, VTextField, VTextFieldNumber, useVForm } from '../../../shared/forms';
import * as yup from 'yup';
import { MunicipiosService } from '../../../shared/services/api/cadastros/MunicipiosService';

interface IFormData {
  idmunicipio: string;
  uf: string;
  cidade: string;  
}

export interface ConfirmationDialogRawProps {
  id: string;
  value: string;
  open: boolean;
  onClose: (value?: string) => void;
}

const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  idmunicipio: yup.string().required().min(7).max(7),
  uf: yup.string().required().min(2).max(2),
  cidade: yup.string().required().max(150), 
});

export const DialogMunicipios: React.FC<ConfirmationDialogRawProps> = (props: ConfirmationDialogRawProps) => {
 
  const { onClose, value: valueProp, open, ...other } = props;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [value, setValue] = useState(valueProp);
  const {formRef, save, } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error'  | 'info'  | 'success'>('warning');  
  
  const [openConfirm, setOpenConfirm] = useState(false);
    
  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event, 
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    if(tms === 'success'){ 
      onClose(valueProp);
    }
  };
  
  const handleDelete = () => {
    setOpenConfirm(true); 
  };
  
  const handleConfirmDelete = () => {
    MunicipiosService.deleteById(props.id)
      .then(result => {
        if (result instanceof Error) {
          setOpenConfirm(false);
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          setOpenConfirm(false);
          setTms('success');
          setMsg('Registro apagado com sucesso!');
          setOpenmsg(true);
        }
      });
  };
  
  const handleCancelDelete = () => {
    setOpenConfirm(false);
  };

  const handleSave = (dados: IFormData) => {
    formValidationSchema.
      validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);

        if (props.id === 'novo') {
          MunicipiosService
            .create(dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              }else {
                setTms('success');
                setMsg('Lançamento Realizado com sucesso!');
                setOpenmsg(true);
              }
            });
        } else {
          MunicipiosService
            .updateById(props.id, dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Alteração concluída com sucesso!');
                setOpenmsg(true);
              }
            });
        }
      })
      .catch((errors: yup.ValidationError) => {
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  useEffect(() => {
    if(props.id !== 'novo'){
      if(open == true){
        setIsLoading(true);
        MunicipiosService.getById(props.id)
          .then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
              setTms('success');
              setMsg('Alteração concluída com sucesso!');
              setOpenmsg(true);
            } else {
              formRef.current?.setData(result);
            }
          });
      }
    }else{
      formRef.current?.setData({
        descricao: '',
      });
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setValue(valueProp);
    }
  }, [valueProp, open]);

  const handleCancel = () => {
    onClose();
  };


  return (
    <Dialog
      open={open}
      {...other}
    >
      <DialogTitle>Município</DialogTitle>
      <DialogContent dividers>
        <VForm ref={formRef}  onSubmit={handleSave}>
          <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg}  autoHideDuration={900} onClose={handleCloseMsg}>
            <Alert
              onClose={handleCloseMsg}
              severity={tms}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {msg}
            </Alert>
          </Snackbar>
          <Snackbar open={openConfirm} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
            <Alert
              severity="warning"  
              variant="filled"
              sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
              action={
                <>
                  <Button color="inherit" size="small" onClick={handleConfirmDelete}>
                        Sim
                  </Button>
                  <Button color="inherit" size="small" onClick={handleCancelDelete}>
                        Nâo
                  </Button>
                </>
              }
            >
              Tem certeza que deseja excluir este registro?
            </Alert>
          </Snackbar>
          <Grid container direction="column" padding={1} >
            <Grid container item direction="row" padding={1} spacing={1}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Grid container item direction="row" spacing={1} > 
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>          
                    <VTextField
                      fullWidth
                      size='small'
                      name='cidade'
                      disabled={isLoading}
                      label='Cidade'
                    />
                  </Grid>   
                  <Grid item xs={9} sm={9} md={9} lg={9} xl={9}>          
                    <VTextFieldNumber
                      fullWidth
                      size='small'
                      name='idmunicipio'
                      disabled={isLoading}
                      label='ID'
                    />
                  </Grid>   
                  <Grid item xs={3} sm={3} md={3} lg={3} xl={3}>
                    <BasicSelectUF
                      name='uf'
                      disabled={isLoading}
                    />
                  </Grid>
     
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </VForm>
      </DialogContent>
      <DialogActions>
        <Button sx={{ marginRight:'20%'}}  color='error' startIcon={<Icon>delete</Icon>}  variant="contained"  onClick={handleDelete}>Excluir</Button>
        <Button variant='outlined' color='error' onClick={handleCancel}>Cancelar</Button>
        <Button variant='outlined' autoFocus onClick={save}>Confirmar</Button>
      </DialogActions>
    </Dialog>
    
  );
}; 